"use client";

import { fetchApi, fetchVoid } from "../club-api/internal";
import {
	type AddMember,
	type CampId,
	type CampStatus,
	type CreateCamp,
	type CreateOrganizer,
	campSchema,
	campSummarySchema,
	type EditionBody,
	type EditionId,
	editionCancelResultSchema,
	editionSchema,
	memberSchema,
	membershipSchema,
	type OrganizerId,
	type OrganizerProfile,
	organizerSchema,
	type RegistrationId,
	rosterRegistrationSchema,
	type SettlementKind,
	type StaffRegistration,
	type StaffRole,
	type UpdateCamp,
	type UserId,
} from "../schemas/camp";
import { cursorPageSchema } from "../schemas/venue/pagination";
import { fetchRosterCsv, type RosterFilter, rosterSearch } from "./csv";

const json = (body: unknown): RequestInit => ({ body: JSON.stringify(body) });
const post = (body?: unknown): RequestInit =>
	body === undefined ? { method: "POST" } : { ...json(body), method: "POST" };
const put = (body: unknown): RequestInit => ({ ...json(body), method: "PUT" });

/** A keyset page request: `cursor` is copied verbatim from a previous `nextCursor`. */
export interface CursorQuery {
	cursor?: string;
	limit?: number;
}

export interface CampListQuery extends CursorQuery {
	status?: CampStatus;
}

export interface RosterQuery extends CursorQuery, RosterFilter {}

function withCursor(fixed: URLSearchParams, { cursor, limit }: CursorQuery): string {
	if (limit !== undefined) fixed.set("limit", String(limit));
	if (cursor !== undefined) fixed.set("cursor", cursor);
	return fixed.toString();
}

const rosterPath = (organizerId: OrganizerId, editionId: EditionId) =>
	`/organizers/${organizerId}/editions/${editionId}/roster`;

export const campApi = {
	camps: {
		archive: (campId: CampId) => fetchApi(`/camps/${campId}/archive`, campSchema, post()),
		create: (organizerId: OrganizerId, body: CreateCamp) =>
			fetchApi(`/organizers/${organizerId}/camps`, campSchema, post(body)),
		get: (campId: CampId) => fetchApi(`/camps/${campId}`, campSchema),
		list: (organizerId: OrganizerId, { status, ...page }: CampListQuery = {}) => {
			const fixed = new URLSearchParams();
			if (status !== undefined) fixed.set("status", status);
			return fetchApi(
				`/organizers/${organizerId}/camps?${withCursor(fixed, page)}`,
				cursorPageSchema(campSummarySchema),
			);
		},
		publish: (campId: CampId) => fetchApi(`/camps/${campId}/publish`, campSchema, post()),
		update: (campId: CampId, body: UpdateCamp) =>
			fetchApi(`/camps/${campId}`, campSchema, put(body)),
	},
	editions: {
		cancel: (editionId: EditionId) =>
			fetchApi(`/editions/${editionId}/cancel`, editionCancelResultSchema, post()),
		close: (editionId: EditionId) =>
			fetchApi(`/editions/${editionId}/close`, editionSchema, post()),
		create: (campId: CampId, body: EditionBody) =>
			fetchApi(`/camps/${campId}/editions`, editionSchema, post(body)),
		get: (editionId: EditionId) => fetchApi(`/editions/${editionId}`, editionSchema),
		list: (campId: CampId, page: CursorQuery = {}) =>
			fetchApi(
				`/camps/${campId}/editions?${withCursor(new URLSearchParams(), page)}`,
				cursorPageSchema(editionSchema),
			),
		open: (editionId: EditionId) => fetchApi(`/editions/${editionId}/open`, editionSchema, post()),
		update: (editionId: EditionId, body: EditionBody) =>
			fetchApi(`/editions/${editionId}`, editionSchema, put(body)),
	},
	members: {
		/** The colleague is named by the email of their OnSand account. */
		add: (organizerId: OrganizerId, body: AddMember) =>
			fetchApi(`/organizers/${organizerId}/members`, memberSchema, post(body)),
		changeRole: (organizerId: OrganizerId, userId: UserId, role: StaffRole) =>
			fetchApi(`/organizers/${organizerId}/members/${userId}`, memberSchema, put({ role })),
		/** Owner-only: the API answers 403 to a manager or staff member. */
		list: (organizerId: OrganizerId, page: CursorQuery = {}) =>
			fetchApi(
				`/organizers/${organizerId}/members?${withCursor(new URLSearchParams(), page)}`,
				cursorPageSchema(memberSchema),
			),
		remove: (organizerId: OrganizerId, userId: UserId) =>
			fetchVoid(`/organizers/${organizerId}/members/${userId}`, { method: "DELETE" }),
	},
	organizers: {
		create: (body: CreateOrganizer) => fetchApi("/organizers", organizerSchema, post(body)),
		get: (organizerId: OrganizerId) => fetchApi(`/organizers/${organizerId}`, organizerSchema),
		list: (page: CursorQuery = {}) =>
			fetchApi(
				`/organizers?${withCursor(new URLSearchParams(), page)}`,
				cursorPageSchema(membershipSchema),
			),
		publish: (organizerId: OrganizerId) =>
			fetchApi(`/organizers/${organizerId}/publish`, organizerSchema, post()),
		updateProfile: (organizerId: OrganizerId, body: OrganizerProfile) =>
			fetchApi(`/organizers/${organizerId}/profile`, organizerSchema, put(body)),
	},
	roster: {
		/** Staff add someone by phone or on site; a full edition waitlists them. */
		add: (organizerId: OrganizerId, editionId: EditionId, body: StaffRegistration) =>
			fetchApi(rosterPath(organizerId, editionId), rosterRegistrationSchema, post(body)),
		/** Frees the seats; the server promotes the next waitlisted party in the same transaction. */
		cancel: (
			organizerId: OrganizerId,
			editionId: EditionId,
			registrationId: RegistrationId,
			reason?: string,
		) =>
			fetchApi(
				`${rosterPath(organizerId, editionId)}/${registrationId}/cancel`,
				rosterRegistrationSchema,
				post({ reason: reason ?? null }),
			),
		exportCsv: fetchRosterCsv,
		list: (organizerId: OrganizerId, editionId: EditionId, query: RosterQuery = {}) =>
			fetchApi(
				`${rosterPath(organizerId, editionId)}?${withCursor(new URLSearchParams(rosterSearch(query)), query)}`,
				cursorPageSchema(rosterRegistrationSchema),
			),
		/** Informational in phase 1: how the organizer was paid, or that the seat is free of charge. */
		setSettlement: (
			organizerId: OrganizerId,
			editionId: EditionId,
			registrationId: RegistrationId,
			kind: SettlementKind,
		) =>
			fetchApi(
				`${rosterPath(organizerId, editionId)}/${registrationId}/settlement`,
				rosterRegistrationSchema,
				put({ kind }),
			),
	},
};
